import { UserRound } from 'lucide-react';
import { Badge, cn } from '@mlm/ui';
import { lineKey } from '../../lib/cart-store';
import type { PosSale } from '../../lib/use-pos-sale';
import { CartLine } from './cart-line';
import { SlotBalances } from './slot-balances';
import { TotalsBox } from './totals-box';

/**
 * The right pane of the till: who is buying, what is in the cart, the running
 * total, and Continue. Payment is NOT here; it lives on the checkout page, so
 * this pane stays a list the cashier can read at a glance.
 */
export function CartPanel({
  sale,
  showPhotos,
  variant = 'pane',
  onSelectCustomer,
  onSwitchSlot,
  onContinue,
}: {
  sale: PosSale;
  showPhotos: boolean;
  /**
   * `pane`: the fixed 420 px column beside the catalog (wide screens).
   * `sheet`: the same content filling a slide-over (narrow screens).
   */
  variant?: 'pane' | 'sheet';
  onSelectCustomer: () => void;
  onSwitchSlot: () => void;
  onContinue: () => void;
}) {
  const { cart, member, slotCode, stockistMode, stockistBranch } = sale;
  const units = cart.reduce((n, l) => n + l.qty, 0);
  // A stockist transfer needs a buyer who actually owns a stockist branch.
  const notAStockist = stockistMode && !!member && !stockistBranch && !sale.stockistBranchLoading;
  const blocked =
    cart.length === 0 || sale.courseBlocked || (stockistMode && !!member && !stockistBranch);

  return (
    <aside
      aria-label={stockistMode ? 'Transfer' : 'Cart'}
      className={cn(
        'flex flex-col bg-card p-[25px]',
        variant === 'pane' ? 'sticky top-0 h-screen w-[420px] shrink-0 border-l' : 'h-full w-full',
      )}
    >
      {/* In the slide-over the sheet's own close button sits top right. */}
      <div className={cn('flex items-center justify-between', variant === 'sheet' && 'pr-10')}>
        <h2 className="text-2xl font-semibold">{stockistMode ? 'Transfer' : 'Cart'}</h2>
        {(cart.length > 0 || member) && (
          <button
            type="button"
            onClick={sale.clearSale}
            className="-mr-2 flex h-11 items-center px-2 text-sm font-medium text-muted-foreground underline"
          >
            Clear sale
          </button>
        )}
      </div>
      <div className="mt-1 flex justify-between text-sm text-muted-foreground">
        <span>Date</span>
        <span>{new Date().toLocaleDateString(undefined, { dateStyle: 'medium' })}</span>
      </div>

      {/* Customer */}
      <div className="mt-4 border-b pb-4">
        {member ? (
          <div className="flex items-start gap-3">
            <div className="min-w-0 flex-1 space-y-0.5">
              <div className="truncate text-base font-semibold">
                {member.fullName ?? member.email}
              </div>
              <div className="truncate text-sm text-muted-foreground">{member.email}</div>
              {slotCode ? (
                <>
                  <div className="flex items-center gap-2 pt-1">
                    <Badge variant="default" className="text-xs">
                      slot {slotCode}
                    </Badge>
                    {/* Hidden in stockist mode: there the slot is not a free
                        choice — the "Sell to Stockist" search BINDS the slot
                        that owns the destination branch, and switching to a
                        sibling slot that owns none only dead-ends checkout. */}
                    {!stockistMode && sale.slots.length > 1 && (
                      <button
                        type="button"
                        className="-my-2 flex h-11 items-center text-sm text-muted-foreground underline"
                        onClick={onSwitchSlot}
                      >
                        Switch slot
                      </button>
                    )}
                  </div>
                  <SlotBalances
                    slot={sale.selectedSlot}
                    showGc={sale.offersGc}
                    showCoin={sale.offersCoin}
                  />
                </>
              ) : sale.needsSlotPick ? (
                // The picker was dismissed without a choice: say so and give
                // the way back in, instead of a dead "No active slot".
                <button
                  type="button"
                  className="flex h-11 items-center text-sm font-medium text-warning underline"
                  onClick={onSwitchSlot}
                >
                  Choose a slot ({sale.slots.length})
                </button>
              ) : (
                <div className="pt-1 text-sm text-warning">No active slot</div>
              )}
            </div>
            <button
              type="button"
              onClick={onSelectCustomer}
              className="-mr-2 -mt-2 flex h-11 shrink-0 items-center px-2 text-sm font-medium underline"
            >
              Change
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={onSelectCustomer}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-[10px] border-2 border-dashed text-base font-medium text-muted-foreground"
          >
            <UserRound className="h-5 w-5" />
            {stockistMode ? 'Select stockist' : 'Select customer'}
          </button>
        )}
        {stockistMode && stockistBranch && (
          <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <span className="font-medium">Stockist stock transfer.</span> These items move into{' '}
            <span className="font-medium">{stockistBranch.name}</span>&apos;s stock, not a retail
            sale.
          </div>
        )}
        {notAStockist && (
          <p className="mt-3 text-sm text-destructive">
            This member does not own a stockist branch. Pick a stockist to transfer stock.
          </p>
        )}
      </div>

      {/* Lines */}
      <div className="-mr-3 min-h-[120px] flex-1 overflow-y-auto pr-3">
        {cart.length === 0 ? (
          <p className="py-8 text-center text-base text-muted-foreground">
            Tap a product to add it.
          </p>
        ) : (
          <ul className="divide-y">
            {cart.map((l) => {
              const key = lineKey(l);
              return (
                <CartLine
                  key={key}
                  line={l}
                  priceMap={sale.priceMap}
                  showPhoto={showPhotos}
                  onStep={(d) => sale.stepQty(key, d)}
                  onSet={(n) => sale.setQty(key, n)}
                  onRemove={() => sale.removeFromCart(key)}
                />
              );
            })}
          </ul>
        )}
      </div>

      {/* Summary */}
      <div className="space-y-3 pt-4">
        {sale.courseNotice && (
          <p className={`text-sm ${sale.courseBlocked ? 'text-destructive' : 'text-warning'}`}>
            {sale.courseNotice}
          </p>
        )}
        <TotalsBox
          rows={[
            {
              label: 'Items',
              value: `${units} ${units === 1 ? 'unit' : 'units'}`,
              tone: 'muted',
            },
          ]}
          total={`₱${sale.goodsTotal.toFixed(2)}`}
        />
        <button
          type="button"
          disabled={member ? blocked : false}
          onClick={member ? onContinue : onSelectCustomer}
          className="h-[50px] w-full rounded-[10px] bg-primary text-base font-semibold text-primary-foreground disabled:opacity-40"
        >
          {member ? 'Continue' : stockistMode ? 'Select stockist' : 'Select customer'}
        </button>
      </div>
    </aside>
  );
}
